import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';
import { ApiEnvelope, Contract, ContractStatus, STATUS_LABEL, formatDateTime } from './contractTypes';

type ContractListPageProps = {
  title: string;
  desc: string;
  statusOptions: ContractStatus[];
  groupFilter: (item: Contract) => boolean;
};

export default function ContractListPage({ title, desc, statusOptions, groupFilter }: ContractListPageProps) {
  const [items, setItems] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | ContractStatus>('');
  const [detailItem, setDetailItem] = useState<Contract | null>(null);
  const [certOpening, setCertOpening] = useState(false);

  // 고유번호증은 비공개 파일이라 관리자 id를 실어 받아온 뒤 새 탭에서 연다.
  const openRegistrationCert = async (item: Contract) => {
    const session = getAdminSession();
    if (!session) {
      window.alert('로그인이 필요합니다.');
      return;
    }
    const tab = window.open('', '_blank');
    setCertOpening(true);
    try {
      const res = await axios.get<Blob>(`${MainURL}/inquiry/${item.id}/registration-cert`, {
        params: { requesterId: session.id },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      tab?.close();
      let message = '고유번호증을 불러오지 못했습니다.';
      if (axios.isAxiosError(err) && err.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await err.response.data.text());
          if (body?.message) message = body.message;
        } catch {
          // ignore
        }
      }
      window.alert(message);
    } finally {
      setCertOpening(false);
    }
  };
  const [mvpModalItem, setMvpModalItem] = useState<Contract | null>(null);
  const [mvpPreviewUrl, setMvpPreviewUrl] = useState('');
  const [mvpNote, setMvpNote] = useState('');
  const [mvpSubmitting, setMvpSubmitting] = useState(false);
  const [mvpError, setMvpError] = useState('');
  const [confirmingDepositKey, setConfirmingDepositKey] = useState<string | null>(null);
  const [confirmingBalanceKey, setConfirmingBalanceKey] = useState<string | null>(null);

  // 2026-09-18부터 신청~MVP확인 단계(inquiries)와 전자서명 이후(online_contracts)가 테이블로
  // 나뉘어서, 이 화면(3개 메뉴가 전부 공유)은 두 목록을 함께 불러와 합친 뒤 기존 groupFilter를
  // 그대로 적용한다. status 드롭다운 필터는 병합 이후 클라이언트에서 한 번 더 좁힌다(서버 쪽
  // status 필터는 각 테이블에만 적용되므로, 병합 전에 넘기면 다른 테이블 쪽이 누락될 수 있어
  // 병합은 항상 무필터로 받고 상태 필터는 이 함수 안에서 처리한다).
  const fetchInquiries = useCallback(async (query: string, statusFilter: '' | ContractStatus) => {
    setLoading(true);
    setErrorMsg('');
    try {
      const [contractsRes, inquiriesRes] = await Promise.allSettled([
        axios.get<ApiEnvelope<Contract[]>>(`${MainURL}/api/admin/contracts/list`, {
          params: query ? { q: query } : {},
        }),
        axios.get<ApiEnvelope<Contract[]>>(`${MainURL}/inquiry`, {
          params: { requesterId: getAdminSession()?.id },
        }),
      ]);

      const merged: Contract[] = [];
      let anyOk = false;
      let firstErrorMsg = '';

      if (contractsRes.status === 'fulfilled' && contractsRes.value.data?.ok && contractsRes.value.data.data) {
        merged.push(...contractsRes.value.data.data.map((item) => ({ ...item, source: 'contract' as const })));
        anyOk = true;
      } else if (contractsRes.status === 'fulfilled') {
        firstErrorMsg = contractsRes.value.data?.message || firstErrorMsg;
      }

      if (inquiriesRes.status === 'fulfilled' && inquiriesRes.value.data?.ok && inquiriesRes.value.data.data) {
        const q = query.trim().toLowerCase();
        const inquiryItems = inquiriesRes.value.data.data
          .map((item) => ({ ...item, source: 'inquiry' as const }))
          .filter(
            (item) =>
              !q ||
              [item.company_name, item.customer_name, item.phone, item.email].some(
                (v) => v && String(v).toLowerCase().includes(q),
              ),
          );
        merged.push(...inquiryItems);
        anyOk = true;
      } else if (inquiriesRes.status === 'fulfilled') {
        firstErrorMsg = firstErrorMsg || inquiriesRes.value.data?.message || '';
      }

      if (!anyOk) {
        setErrorMsg(firstErrorMsg || '신청 목록을 불러오지 못했습니다.');
        setItems([]);
        return;
      }

      const filtered = merged.filter(
        (item) => (!statusFilter || item.status === statusFilter) && groupFilter(item),
      );
      filtered.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setItems(filtered);
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : null;
      setErrorMsg(msg || '신청 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchInquiries(q, status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchInquiries(q, status);
  };

  // 두 테이블(inquiries/online_contracts)의 id가 서로 독립적으로 증가하므로 숫자 id만으로는
  // 같은 화면에서 행을 특정할 수 없다 - source까지 합친 문자열 키로 항상 함께 다룬다.
  const itemKey = (item: Pick<Contract, 'id' | 'source'>) => `${item.source}:${item.id}`;
  const apiBaseFor = (source: Contract['source']) => (source === 'inquiry' ? `${MainURL}/inquiry` : `${MainURL}/api/admin/contracts`);

  const handleDelete = async (item: Contract) => {
    if (!window.confirm('이 신청을 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${apiBaseFor(item.source)}/${item.id}`, {
        params: { requesterId: getAdminSession()?.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((row) => itemKey(row) !== itemKey(item)));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : null;
      alert(msg || '삭제하지 못했습니다.');
    }
  };

  const handleConfirmDeposit = async (item: Contract) => {
    const session = getAdminSession();
    if (!session) {
      alert('관리자 세션이 없습니다. 다시 로그인해 주세요.');
      return;
    }
    if (!window.confirm('입금을 확인 처리하시겠습니까?')) return;

    const key = itemKey(item);
    setConfirmingDepositKey(key);
    try {
      const res = await axios.post<ApiEnvelope<Omit<Contract, 'source'>>>(
        `${apiBaseFor(item.source)}/${item.id}/confirm-deposit`,
        { requesterId: session.id },
      );
      if (res.data?.ok && res.data.data) {
        const updated: Contract = { ...res.data.data, source: item.source };
        setItems((prev) => prev.map((row) => (itemKey(row) === key ? updated : row)).filter(groupFilter));
      } else {
        alert(res.data?.message || '입금 확인 처리에 실패했습니다.');
      }
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'error' in data
          ? String((data as { error?: { message?: string } }).error?.message)
          : null;
      alert(msg || '입금 확인 처리에 실패했습니다.');
    } finally {
      setConfirmingDepositKey(null);
    }
  };

  // 잔금 확인은 status==='signed' 행에서만 노출되는데, 2026-09-18 변경으로 signed는 항상
  // online_contracts(source='contract')에서만 나오므로 apiBaseFor(item.source)가 사실상
  // 항상 /api/admin/contracts로 귀결된다.
  const handleConfirmBalance = async (item: Contract) => {
    const session = getAdminSession();
    if (!session) {
      alert('관리자 세션이 없습니다. 다시 로그인해 주세요.');
      return;
    }
    if (!window.confirm('잔금 입금을 확인 처리하시겠습니까?')) return;

    const key = itemKey(item);
    setConfirmingBalanceKey(key);
    try {
      const res = await axios.post<ApiEnvelope<Omit<Contract, 'source'>>>(
        `${apiBaseFor(item.source)}/${item.id}/confirm-balance`,
        { requesterId: session.id },
      );
      if (res.data?.ok && res.data.data) {
        const updated: Contract = { ...res.data.data, source: item.source };
        setItems((prev) => prev.map((row) => (itemKey(row) === key ? updated : row)).filter(groupFilter));
      } else {
        alert(res.data?.message || '잔금 입금 확인 처리에 실패했습니다.');
      }
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'error' in data
          ? String((data as { error?: { message?: string } }).error?.message)
          : null;
      alert(msg || '잔금 입금 확인 처리에 실패했습니다.');
    } finally {
      setConfirmingBalanceKey(null);
    }
  };

  const openMvpModal = (item: Contract) => {
    setMvpModalItem(item);
    setMvpPreviewUrl(item.mvp_preview_url || '');
    setMvpNote(item.mvp_note || '');
    setMvpError('');
  };

  const closeMvpModal = () => {
    setMvpModalItem(null);
  };

  const submitMvpReady = async () => {
    if (!mvpModalItem) return;
    const session = getAdminSession();
    if (!session) {
      setMvpError('관리자 세션이 없습니다. 다시 로그인해 주세요.');
      return;
    }

    setMvpSubmitting(true);
    setMvpError('');
    try {
      const res = await axios.post<ApiEnvelope<unknown>>(
        `${apiBaseFor(mvpModalItem.source)}/${mvpModalItem.id}/mvp-ready`,
        { requesterId: session.id, previewUrl: mvpPreviewUrl, note: mvpNote },
      );
      if (!res.data?.ok) {
        throw new Error(res.data?.message || 'MVP 준비완료 처리에 실패했습니다.');
      }
      closeMvpModal();
      setDetailItem(null);
      fetchInquiries(q, status);
    } catch (err) {
      const data = axios.isAxiosError(err) ? err.response?.data : null;
      const msg =
        data && typeof data === 'object' && 'error' in data
          ? String((data as { error?: { message?: string } }).error?.message)
          : null;
      setMvpError(msg || 'MVP 준비완료 처리에 실패했습니다.');
    } finally {
      setMvpSubmitting(false);
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">{title}</h2>
          <p className="admin-panel-desc">{desc}</p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={() => fetchInquiries(q, status)} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-filterbar" onSubmit={handleSearch}>
        <input
          type="text"
          placeholder="교회명 · 담당자 · 연락처 검색"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value as '' | ContractStatus)}>
          <option value="">전체 상태</option>
          {statusOptions.map((value) => (
            <option key={value} value={value}>
              {STATUS_LABEL[value]}
            </option>
          ))}
        </select>
        <button type="submit" className="admin-refresh-btn">검색</button>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>접수일</th>
              <th>교회(회사)명</th>
              <th>담당자</th>
              <th>연락처</th>
              <th>이메일</th>
              <th>상태</th>
              <th>입금여부</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={8}>해당하는 신청 건이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={itemKey(item)}>
                  <td>{formatDateTime(item.created_at)}</td>
                  <td>{item.company_name || item.contract_name || '-'}</td>
                  <td>{item.representative || item.customer_name}</td>
                  <td>{item.phone}</td>
                  <td>{item.email || '-'}</td>
                  <td>
                    <span className={`admin-status-tag admin-status-tag--${item.status}`}>
                      {STATUS_LABEL[item.status] ?? item.status}
                    </span>
                  </td>
                  <td>
                    {item.deposit_confirmed_at ? (
                      <span className="admin-status-tag admin-status-tag--signed">
                        입금확인 ({formatDateTime(item.deposit_confirmed_at)})
                      </span>
                    ) : (
                      <span className="admin-status-tag admin-status-tag--pending">미확인</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="admin-btn view-btn"
                      onClick={() => setDetailItem(item)}
                    >
                      상세
                    </button>
                    {!item.deposit_confirmed_at ? (
                      <button
                        type="button"
                        className="admin-btn view-btn"
                        onClick={() => handleConfirmDeposit(item)}
                        disabled={confirmingDepositKey === itemKey(item)}
                      >
                        {confirmingDepositKey === itemKey(item) ? '처리 중...' : '입금확인'}
                      </button>
                    ) : item.status === 'deposit_paid' ? (
                      <button
                        type="button"
                        className="admin-btn view-btn"
                        onClick={() => openMvpModal(item)}
                      >
                        MVP 준비처리
                      </button>
                    ) : item.status === 'mvp_ready' ? (
                      <button
                        type="button"
                        className="admin-btn view-btn"
                        onClick={() => openMvpModal(item)}
                      >
                        시안 수정
                      </button>
                    ) : item.status === 'signed' && item.plan_type === 'purchase' ? (
                      <button
                        type="button"
                        className="admin-btn view-btn"
                        onClick={() => handleConfirmBalance(item)}
                        disabled={confirmingBalanceKey === itemKey(item)}
                      >
                        {confirmingBalanceKey === itemKey(item) ? '처리 중...' : '잔금입금확인'}
                      </button>
                    ) : (
                      ['mvp_confirmed', 'signed'].includes(item.status) && (
                        <span className="admin-subtext">결제대기</span>
                      )
                    )}
                    <button
                      type="button"
                      className="admin-btn delete-btn"
                      onClick={() => handleDelete(item)}
                    >
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {detailItem && (
        <div className="modal-overlay" onClick={() => setDetailItem(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>신청 상세</h3>
              <button type="button" onClick={() => setDetailItem(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>접수일</label>
                <p>{formatDateTime(detailItem.created_at)}</p>
              </div>
              <div className="form-group">
                <label>상태</label>
                <p>{STATUS_LABEL[detailItem.status] ?? detailItem.status}</p>
              </div>
              <div className="form-group">
                <label>입금여부</label>
                <p>
                  {detailItem.deposit_confirmed_at
                    ? `확인됨 (${formatDateTime(detailItem.deposit_confirmed_at)}${detailItem.deposit_confirmed_by ? ` · ${detailItem.deposit_confirmed_by}` : ''})`
                    : '미확인'}
                </p>
              </div>
              {detailItem.plan_type === 'purchase' && (
                <div className="form-group">
                  <label>잔금입금여부</label>
                  <p>
                    {detailItem.balance_confirmed_at
                      ? `확인됨 (${formatDateTime(detailItem.balance_confirmed_at)}${detailItem.balance_confirmed_by ? ` · ${detailItem.balance_confirmed_by}` : ''})`
                      : '미확인'}
                  </p>
                </div>
              )}
              <div className="form-group">
                <label>교회명</label>
                <p>{detailItem.company_name || detailItem.contract_name || '-'}</p>
              </div>
              <div className="form-group">
                <label>담당자</label>
                <p>{detailItem.representative || detailItem.customer_name}</p>
              </div>
              <div className="form-group">
                <label>이메일</label>
                <p>{detailItem.email || '-'}</p>
              </div>
              <div className="form-group">
                <label>연락처</label>
                <p>{detailItem.phone}</p>
              </div>
              {detailItem.source === 'inquiry' && (
                <div className="form-group">
                  <label>관리자 로그인 이메일</label>
                  <p>{detailItem.admin_email || detailItem.email || '-'}</p>
                </div>
              )}
              <div className="form-group">
                <label>성도 규모</label>
                <p>{detailItem.member_size || '-'}</p>
              </div>
              <div className="form-group">
                <label>사용 앱</label>
                <p>
                  {/* 2026-09-27 요금제 개편 이후 selected_admin_tabs엔 추가 앱 이름이 들어간다(옛 신청은 탭 이름) */}
                  {[...detailItem.selected_member_tabs, ...detailItem.selected_admin_tabs].join(', ') || '기본(성도용 앱 + PC 관리자)'}
                  {detailItem.sundayschool_addon && !detailItem.selected_admin_tabs.includes('주일학교앱')
                    ? ' (+주일학교)'
                    : ''}
                </p>
              </div>
              <div className="form-group">
                <label>요금제</label>
                <p>
                  {detailItem.plan_type === 'purchase'
                    ? '구축제'
                    : detailItem.plan_type === 'subscription'
                      ? '구독제'
                      : '-'}
                  {detailItem.monthly_amount != null && ` · 월 ${detailItem.monthly_amount.toLocaleString()}원`}
                </p>
              </div>
              <div className="form-group">
                <label>메인 색상</label>
                <p>{detailItem.design_concept || '-'}</p>
              </div>
              <div className="form-group">
                <label>교회로고</label>
                {detailItem.logo_url ? (
                  <p>
                    <img
                      src={`${MainURL}${detailItem.logo_url}`}
                      alt="교회로고"
                      style={{ maxWidth: 160, maxHeight: 160, objectFit: 'contain', display: 'block' }}
                    />
                  </p>
                ) : (
                  <p>-</p>
                )}
              </div>
              {detailItem.source === 'inquiry' && (
                <div className="form-group">
                  <label>테스트 기기</label>
                  <p>
                    안드로이드: {detailItem.test_platforms?.includes('android') ? '선택' : '-'}
                    <br />
                    아이폰(Apple 계정): {detailItem.test_platforms?.includes('ios') ? (detailItem.test_apple_emails?.join(', ') || '선택') : '-'}
                  </p>
                </div>
              )}
              {detailItem.source === 'inquiry' && (
                <div className="form-group">
                  <label>개발자 계정 등록 방식</label>
                  <p>
                    {detailItem.developer_account_method === 'self'
                      ? '직접 등록'
                      : detailItem.developer_account_method === 'shared'
                        ? '구글·애플 계정 공유 후 개발자 계정 등록 대행'
                        : detailItem.developer_account_method === 'agency'
                          ? '모두 대행'
                          : '미선택'}
                  </p>
                </div>
              )}
              {detailItem.source === 'inquiry' && (
                <div className="form-group">
                  <label>고유번호증</label>
                  {detailItem.registration_cert_name ? (
                    <p>
                      {detailItem.registration_cert_name}{' '}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => openRegistrationCert(detailItem)}
                        disabled={certOpening}
                        style={{ marginLeft: 8 }}
                      >
                        {certOpening ? '여는 중...' : '보기'}
                      </button>
                    </p>
                  ) : (
                    <p>-</p>
                  )}
                </div>
              )}
              <div className="form-group">
                <label>메시지</label>
                <p style={{ whiteSpace: 'pre-wrap' }}>{detailItem.applicant_message || '-'}</p>
              </div>
              <div className="form-group">
                <label>MVP 시안</label>
                <p>
                  링크:{' '}
                  {detailItem.mvp_preview_url ? (
                    <a href={detailItem.mvp_preview_url} target="_blank" rel="noopener noreferrer">
                      {detailItem.mvp_preview_url}
                    </a>
                  ) : (
                    '-'
                  )}
                </p>
                <p style={{ whiteSpace: 'pre-wrap' }}>메모: {detailItem.mvp_note || '-'}</p>
                <p>
                  고객 확인:{' '}
                  {detailItem.mvp_confirmed_at ? `확인됨 (${formatDateTime(detailItem.mvp_confirmed_at)})` : '미확인'}
                </p>
                {(detailItem.status === 'deposit_paid' || detailItem.status === 'mvp_ready') ? (
                  <button type="button" className="btn btn-secondary" onClick={() => openMvpModal(detailItem)}>
                    {detailItem.status === 'deposit_paid' ? '시안 입력 (MVP 준비처리)' : '시안 수정'}
                  </button>
                ) : (
                  !detailItem.deposit_confirmed_at && (
                    <p className="admin-subtext">기본요금 입금 확인 후 시안을 입력할 수 있습니다.</p>
                  )
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDetailItem(null)}>
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {mvpModalItem != null && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
          }}
        >
          <div
            style={{
              background: '#fff',
              padding: 28,
              borderRadius: 8,
              width: 420,
              maxWidth: '90vw',
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {mvpModalItem.status === 'mvp_ready' ? '시안 정보 수정' : 'MVP 준비완료 처리'}
            </h3>
            <p style={{ color: '#666', fontSize: 14 }}>
              시안 미리보기 URL과 담당자 메모를 입력하면, 고객이 MVP 확인 페이지(/mvp-confirm)에서 이 정보를
              보고 확인 여부를 결정할 수 있습니다. 고객이 확인 완료를 누르기 전까지는 수정할 수 있습니다.
            </p>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>미리보기 URL</span>
              <input
                type="text"
                value={mvpPreviewUrl}
                onChange={(e) => setMvpPreviewUrl(e.target.value)}
                placeholder="https://..."
                style={{ width: '100%', padding: 8 }}
              />
            </label>
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>메모</span>
              <textarea
                value={mvpNote}
                onChange={(e) => setMvpNote(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: 8 }}
              />
            </label>
            {mvpError && <p className="admin-error-text">{mvpError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="admin-refresh-btn" onClick={closeMvpModal}>
                취소
              </button>
              <button
                type="button"
                className="admin-refresh-btn"
                onClick={submitMvpReady}
                disabled={mvpSubmitting}
              >
                {mvpSubmitting ? '처리 중...' : mvpModalItem.status === 'mvp_ready' ? '저장' : '준비완료 처리'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
